import { InputWithLabel, AutocompleteWithLabel } from '@adotel/components'
import { useQuery } from '@apollo/client'
import { FormikErrors, FormikTouched } from 'formik'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import SectionTitle from '../../components/SectionTitle'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { getTranslate } from '../../localization'
import { RoomType, RoomTypeBrief } from '../../types/roomtype'

interface RoomtypeVirtualRoomDetailsProps {
  values: RoomType
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
  errors: FormikErrors<RoomType>
  touched: FormikTouched<RoomType>
  handleBlur: any
  isSubmitting: any
  classes: any
}

export const RoomtypeVirtualRoomDetails: React.FC<
  RoomtypeVirtualRoomDetailsProps
> = ({
  values,
  setFieldValue,
  errors,
  touched,
  handleBlur,
  isSubmitting,
  classes
}) => {
  const params = useParams<{
    accommodationID: string
    roomTypeCode: string
    propertyCode: string
    roomTypeId: string
  }>()

  const [
    autoCompleteForParentRoomTypeCode,
    setAutoCompleteForParentRoomTypeCode
  ] = useState<any>(null)

  const { data: roomtypes, loading: loadingForRoomtypes } = useQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      propertyCode: params.propertyCode,
      showDeletedRoomTypes: false,
      showDeactivedRoomTypes: false
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  useEffect(() => {
    if (
      autoCompleteForParentRoomTypeCode &&
      autoCompleteForParentRoomTypeCode.code
    ) {
      setFieldValue(
        'virtualRoomDetails.parentRoomTypeCode',
        autoCompleteForParentRoomTypeCode.code
      )
    }
  }, [autoCompleteForParentRoomTypeCode])

  useEffect(() => {
    if (!!params.roomTypeId) {
      const foundRoomType = roomtypes?.roomTypes.find(
        (rt) =>
          rt.displayRoomTypeCode ===
          values.virtualRoomDetails.parentRoomTypeCode
      )

      setAutoCompleteForParentRoomTypeCode({
        label: foundRoomType?.name,
        value: foundRoomType?.id,
        code: foundRoomType?.displayRoomTypeCode
      })
    }
  }, [loadingForRoomtypes])

  return (
    <>
      <div className={classes.titleContainer}>
        <SectionTitle title="Virtual Room Details" marginBottom />
      </div>
      <div className={classes.fieldsContainer}>
        <AutocompleteWithLabel
          label={getTranslate('Parent Room Type')}
          value={autoCompleteForParentRoomTypeCode}
          fullWidth
          placeholder={getTranslate('Choose RoomType')}
          options={
            roomtypes?.roomTypes
              .filter((ele) => ele.type === 'Physical')
              .map((item) => ({
                label: `${item.name} (${item.displayRoomTypeCode})`,
                value: item.id || '',
                code: item.displayRoomTypeCode
              })) || []
          }
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForParentRoomTypeCode({
                value: v.value,
                label: v.label,
                code: v.code
              })
            } else {
              setAutoCompleteForParentRoomTypeCode(null)
            }
          }}
          inputSize="small"
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          loading={loadingForRoomtypes}
          loadingText={getTranslate('Loading') + '...'}
          error={
            (touched.virtualRoomDetails?.parentRoomTypeCode &&
              !!errors.virtualRoomDetails?.parentRoomTypeCode) ||
            undefined
          }
          helpertext={
            touched.virtualRoomDetails?.parentRoomTypeCode
              ? getTranslate(
                  errors.virtualRoomDetails?.parentRoomTypeCode || ''
                )
              : undefined
          }
          disabled={!!params.roomTypeId}
        />
        <InputWithLabel
          name="virtualRoomDetails.virtualRoomAvailabilityMultiplier"
          label={getTranslate('Virtual Room Availability Multiplier')}
          placeholder={getTranslate('example: ') + 0.5}
          autoComplete="off"
          value={values.virtualRoomDetails.virtualRoomAvailabilityMultiplier}
          onChange={(event) => {
            const inputValue = event.currentTarget.value

            // Regex to allow only valid float numbers (e.g., "0", "0.5", "0.99") and prevent invalid characters like "0.5a"
            if (/^(\d(\.\d{0,2})?)?$/.test(inputValue) && inputValue <= '1') {
              setFieldValue(
                'virtualRoomDetails.virtualRoomAvailabilityMultiplier',
                inputValue
              )
            }
          }}
          error={
            (touched.virtualRoomDetails?.virtualRoomAvailabilityMultiplier &&
              !!errors.virtualRoomDetails?.virtualRoomAvailabilityMultiplier) ||
            undefined
          }
          helpertext={
            touched.virtualRoomDetails?.virtualRoomAvailabilityMultiplier
              ? getTranslate(
                  errors.virtualRoomDetails
                    ?.virtualRoomAvailabilityMultiplier || ''
                )
              : undefined
          }
          onBlur={handleBlur}
          disabled={isSubmitting}
          inputSize="small"
        />
      </div>
    </>
  )
}
